import { pageTitle } from '@palaccount/config';
import {
  RECEIVE_WITHOUT_ORDER,
  type CreateGrnRequest,
  type GoodsReceiptResponse,
  type GrnLineInput,
  type UpdateGrnRequest,
} from '@palaccount/contracts';
import { PackageCheck, Plus, ReceiptText, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { Amount, Ltr, Qty } from '@/components/amount';
import { DeleteConfirmBox } from '@/components/confirm-box';
import { DocumentHistoryCard } from '@/components/document-history-card';
import { useUnsavedChanges } from '@/components/document-header-controls';
import { DocumentToolbar } from '@/components/document-toolbar';
import { AttachmentsPanel } from '@/features/attachments/attachments-panel';
import { DocumentActions } from '@/features/documents/document-actions';
import { goodsReceiptDocument } from '@/features/documents/record-documents';
import { Gated } from '@/components/permission-gate';
import { PersonSelect } from '@/components/person-select';
import { ReasonBox } from '@/components/reason-box';
import { DocStatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Typeahead } from '@/components/ui/typeahead';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { trimZeros } from '@/features/inventory/conversion';
import { toMilliQty } from '@/features/inventory/stock-format';
import { ItemCell } from '@/features/inventory/item-cell';
import { useItemPicker } from '@/features/inventory/item-picker';
import { useWarehouses } from '@/features/inventory/stock-api';
import { SupplierBalanceChip } from '@/features/partners/supplier-balance-chip';
import { usePartnerPicker } from '@/features/partners/partner-picker';
import { PartnerTypeahead } from '@/features/partners/partner-typeahead';
import { denyPermission, useCan } from '@/lib/permissions';
import { ApiError, localizeServerCause } from '@/lib/server-messages';
import { cn } from '@/lib/utils';
import { useBaseCurrency, useDocumentDate } from '@/lib/session';

import {
  useCreateGoodsReceipt,
  useCancelGoodsReceipt,
  useDeleteGoodsReceipt,
  useGoodsReceipt,
  useGoodsReceiptHistory,
  usePostGoodsReceipt,
  usePurchaseOrderPicker,
  useUpdateGoodsReceipt,
} from './api';
import {
  acceptedQtyFor,
  acceptedValueOf,
  isBackorder,
  lineValueOf,
  parseSerials,
  receiptIsLate,
  rejectedLineCount,
  type GrnQuality,
} from './grn-rules';

interface EditLine {
  /** A stable React key — the order line number, or a client id for a free line. */
  key: string;
  /** The order line this row receives; null on a free line (wave 5C's direct receipt). */
  poLineNo: number | null;
  itemId: string;
  itemCode: string;
  description: string;
  /**
   * Wave 5C — the row's own warehouse, '' meaning "the header's" (the server
   * default). Goods from one lorry can land in two places.
   */
  warehouseId: string;
  /** The ORDER's net price on an order line; typed on a free line. 6 dp. */
  unitCost: string;
  ordered: string;
  /**
   * Wave 1C — what the OTHER posted receipts already took, and what was
   * still outstanding for this one (server-computed; the backorder flag
   * compares against `remaining`, not `ordered`).
   */
  alreadyReceived: string;
  remaining: string;
  received: string;
  /**
   * `N-4` — the unit all three of this row's figures are in. It comes off the
   * LINE: a GRN is raised against a fixed purchase order and this grid has no
   * item picker, so the server's resolution cannot go stale under it.
   */
  unitSymbol: string;
  unitPrecision: number;
  quality: GrnQuality;
  acceptedTyped: string;
  serialsText: string;
  batchNo: string;
  note: string;
}

function fromReceipt(receipt: GoodsReceiptResponse): EditLine[] {
  return receipt.lines.map((line) => ({
    key: line.poLineNo === null ? `free-${line.id}` : `po-${line.poLineNo}`,
    poLineNo: line.poLineNo,
    itemId: line.itemId,
    itemCode: line.itemCode,
    description: line.description,
    warehouseId: line.warehouseId === receipt.warehouseId ? '' : line.warehouseId,
    unitCost: trimZeros(line.unitCost),
    ordered: trimZeros(line.qtyOrdered),
    alreadyReceived: trimZeros(line.alreadyReceivedQty),
    remaining: trimZeros(line.remainingQty),
    received: trimZeros(line.qtyReceived),
    unitSymbol: line.unitSymbol,
    unitPrecision: line.unitPrecision,
    quality: line.quality,
    acceptedTyped: line.quality === 'partially_accepted' ? trimZeros(line.acceptedQty) : '',
    serialsText: (line.serialNos ?? []).join(', '),
    batchNo: line.batchNo ?? '',
    note: line.note ?? '',
  }));
}

/** Wave 5C — the header's lorry paper, as the boxes hold it ('' = unset). */
interface HeaderFields {
  supplierDeliveryNoteNo: string;
  receivedByUserId: string;
  checkedByUserId: string;
  driver: string;
  vehicleNo: string;
  manualRef: string;
  bookNo: string;
}

const EMPTY_HEADER: HeaderFields = {
  supplierDeliveryNoteNo: '',
  receivedByUserId: '',
  checkedByUserId: '',
  driver: '',
  vehicleNo: '',
  manualRef: '',
  bookNo: '',
};

/** Create sends only what was typed; the server defaults Received by to the creator. */
function headerForCreate(header: HeaderFields): Partial<CreateGrnRequest> {
  const out: Partial<CreateGrnRequest> = {};
  const deliveryNoteNo = header.supplierDeliveryNoteNo.trim();
  const driver = header.driver.trim();
  const vehicleNo = header.vehicleNo.trim();
  const manualRef = header.manualRef.trim();
  const bookNo = header.bookNo.trim();
  if (deliveryNoteNo !== '') out.supplierDeliveryNoteNo = deliveryNoteNo;
  if (header.receivedByUserId !== '') out.receivedByUserId = header.receivedByUserId;
  if (header.checkedByUserId !== '') out.checkedByUserId = header.checkedByUserId;
  if (driver !== '') out.driver = driver;
  if (vehicleNo !== '') out.vehicleNo = vehicleNo;
  if (manualRef !== '') out.manualRef = manualRef;
  if (bookNo !== '') out.bookNo = bookNo;
  return out;
}

/** Update sends every box: an emptied box clears the stored value (null). */
function headerForUpdate(header: HeaderFields): Partial<UpdateGrnRequest> {
  const text = (value: string) => (value.trim() === '' ? null : value.trim());
  return {
    supplierDeliveryNoteNo: text(header.supplierDeliveryNoteNo),
    receivedByUserId: header.receivedByUserId || null,
    checkedByUserId: header.checkedByUserId || null,
    driver: text(header.driver),
    vehicleNo: text(header.vehicleNo),
    manualRef: text(header.manualRef),
    bookNo: text(header.bookNo),
  };
}

function CellInput({ mono, className, ...props }: React.ComponentProps<'input'> & { mono?: boolean }) {
  return (
    <input
      className={cn(
        'h-9 w-full min-w-0 rounded-md border border-transparent bg-transparent px-2 text-sm outline-none transition-colors',
        'hover:border-input focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
        'disabled:cursor-not-allowed disabled:opacity-70',
        mono && 'text-end font-mono tabular-nums',
        className,
      )}
      {...props}
    />
  );
}

/**
 * PUR-02's editor — used while the lorry is being unloaded. Creating a
 * receipt IS "Load from PO": the server fills every item-linked order line
 * at its remaining quantity, and the clerk then lowers what did not arrive,
 * sets the quality checks (the Accepted-qty column resolves BR-PUR-02-04's
 * ambiguity) and names the serials or lots BR-MD-03's tracking demands.
 * Post — add to stock is the once-only boundary (BR-PUR-02-07/-08).
 *
 * ── THE LATE FLAG (BR-PUR-02-11), BUILT WHERE A PRIOR MARK STOOD ────────────
 *
 * "An actual delivery date later than the expected date must be flagged, so
 * late suppliers can be found. The seed is a day late and nothing says so."
 * A prior wave ignore-marked this here as UNENFORCED — both dates existed and
 * were never compared, and its fence stopped at the purchases screens, so it
 * reported the server half instead of reaching for it. That is now the
 * history of this block, not its state: the goods-receipt response carries
 * the order's `expectedDate` (BR-PUR-01-10's field, joined in the
 * goods-receipts service), `receiptIsLate` in grn-rules.ts is the one
 * predicate both screens share, the receipts LIST wears the chip beside the
 * date — finding late suppliers is a scanning job — and this editor wears it
 * in the toolbar, live against the date box.
 *
 * STILL NOT THE SAME AS BR-PUR-01-10. That rule flags an ORDER whose expected
 * date has passed with nothing received (orders-list-page.tsx draws it); it
 * stops applying the moment goods arrive, which is exactly when this rule
 * starts. A supplier who delivers everything a week late now trips the
 * second, on every receipt.
 */
export function ReceiptEditorPage() {
  // GEN-107: what these figures are IN, from the session rather than a
  // literal. Every one of them is a ledger or stock valuation, which the
  // company holds in its base currency; nothing here converts, so GEN-116
  // keeps the disclosure off the screen.
  const baseCurrency = useBaseCurrency();
  const { t, i18n } = useTranslation();
  // The reader's language is the PDF's; `print.language` may override it in
  // the template (INV-H6), for the worker and the browser alike.
  const printLocale = i18n.language.startsWith('ar') ? ('ar' as const) : ('en' as const);
  const navigate = useNavigate();
  const { receiptId } = useParams<{ receiptId: string }>();
  const [searchParams] = useSearchParams();
  const isNew = !receiptId;

  const { data: receipt, isPending: loading } = useGoodsReceipt(receiptId);
  const history = useGoodsReceiptHistory(receiptId);

  const { data: warehouses } = useWarehouses();
  const createReceipt = useCreateGoodsReceipt();
  const updateReceipt = useUpdateGoodsReceipt(receiptId ?? '');
  const deleteReceipt = useDeleteGoodsReceipt(receiptId ?? '');
  const postReceipt = usePostGoodsReceipt(receiptId ?? '');
  const cancelReceipt = useCancelGoodsReceipt(receiptId ?? '');

  const canEdit = useCan(isNew ? 'purchasing.create' : 'purchasing.edit');
  /**
   * Wave 5C — a receipt WITHOUT a purchase order is its own switch
   * (`purchasing.receive_without_order`): the shape is offered only to a
   * role that holds it, and the server refuses it by name regardless.
   */
  const canDirect = useCan(RECEIVE_WITHOUT_ORDER);

  // Wave 1C — arriving from the order's "Create receipt" (`?poId=`) seeds the
  // order; the picker still DISPLAYS whatever is held (GEN-50).
  const [purchaseOrderId, setPurchaseOrderId] = useState(() =>
    isNew ? (searchParams.get('poId') ?? '') : '',
  );
  /** Wave 5C — "From purchase order" or "Direct from supplier" (new only). */
  const [mode, setMode] = useState<'order' | 'direct'>('order');
  const [partnerId, setPartnerId] = useState('');
  const [receiptDate, setReceiptDate] = useDocumentDate();
  const [warehouseId, setWarehouseId] = useState('');
  const [supplierRef, setSupplierRef] = useState('');
  /**
   * Wave 5C — the lorry's paper. Received by defaults to the signed-in
   * person on a new receipt (the server does the same when nobody is named);
   * Checked by is a fact, never assumed.
   */
  const [header, setHeader] = useState<HeaderFields>(EMPTY_HEADER);
  const [lines, setLines] = useState<EditLine[]>([]);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [posted, setPosted] = useState(false);
  const [busy, setBusy] = useState(false);
  /** GEN-72's ask-why dialog for the posted-receipt cancel. */
  const [cancelAsk, setCancelAsk] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const loadedId = useRef<string | null>(null);
  /** Client keys for free lines added before they are saved (wave 5C). */
  const freeLineSeq = useRef(0);
  /** S4 — the header rail's ‹‹ ›› wait while there is typed work (see `useUnsavedChanges`). */
  const dirty = useUnsavedChanges(
    [purchaseOrderId, partnerId, receiptDate, warehouseId, supplierRef, header, lines, notes],
    isNew ? 'new' : receipt && loadedId.current === receipt.id ? receipt.id : null,
  );

  const status = receipt?.status ?? 'draft';
  const isDraft = status === 'draft';
  const editable = isDraft && canEdit && !isNew;
  /** The header boxes type on a new receipt and on an editable draft. */
  const headerLocked = !isNew && !editable;
  /** Wave 5C — the receipt's shape: the toggle on a new one, the record's own after. */
  const isDirect = isNew ? mode === 'direct' && canDirect : (receipt?.direct ?? false);

  useEffect(() => {
    document.title = pageTitle(t('grn.editorTitle'));
  }, [t]);

  useEffect(() => {
    if (receipt && loadedId.current !== receipt.id) {
      loadedId.current = receipt.id;
      setReceiptDate(receipt.receiptDate);
      setWarehouseId(receipt.warehouseId);
      setSupplierRef(receipt.supplierRef ?? '');
      setHeader({
        supplierDeliveryNoteNo: receipt.supplierDeliveryNoteNo ?? '',
        receivedByUserId: receipt.receivedByUserId ?? '',
        checkedByUserId: receipt.checkedByUserId ?? '',
        driver: receipt.driver ?? '',
        vehicleNo: receipt.vehicleNo ?? '',
        manualRef: receipt.manualRef ?? '',
        bookNo: receipt.bookNo ?? '',
      });
      setLines(fromReceipt(receipt));
      setNotes(receipt.notes ?? '');
    }
  }, [receipt]);

  /*
   * GEN-50 — receivable orders (sent, or already part-received: two
   * containers, two GRNs), SEARCHED rather than listed. The narrowing is the
   * server's `receivable` predicate, not a `.filter()` over a page.
   */
  const orderPicker = usePurchaseOrderPicker(purchaseOrderId || null, { receivable: true });
  /* Wave 5C — the direct receipt's own two pickers: the supplier (GEN-50,
     searched), and the catalogue for its free lines (stock items only — a
     service never enters stock, BR-MD-03-07). */
  const supplierPicker = usePartnerPicker(partnerId || null, { type: 'supplier' });
  const itemPicker = useItemPicker(
    lines.map((line) => line.itemId),
    { stockable: true },
  );
  const activeWarehouses = (warehouses ?? []).filter((warehouse) => warehouse.isActive);

  const setLine = (key: string, patch: Partial<EditLine>) => {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  /** Wave 5C — a free line on a direct receipt: the item is picked, the rest typed. */
  const addFreeLine = () => {
    freeLineSeq.current += 1;
    setLines((current) => [
      ...current,
      {
        key: `new-${freeLineSeq.current}`,
        poLineNo: null,
        itemId: '',
        itemCode: '',
        description: '',
        warehouseId: '',
        unitCost: '',
        ordered: '0',
        alreadyReceived: '0',
        remaining: '0',
        received: '',
        unitSymbol: '',
        unitPrecision: 3,
        quality: 'accepted',
        acceptedTyped: '',
        serialsText: '',
        batchNo: '',
        note: '',
      },
    ]);
  };
  const removeLine = (key: string) => {
    setLines((current) => current.filter((line) => line.key !== key));
  };
  const onPickItem = (key: string, itemId: string) => {
    const item = itemPicker.byId.get(itemId);
    setLine(key, {
      itemId,
      itemCode: item?.code ?? '',
      description: item?.name ?? '',
      // The cost the server will default to is the item's last posted bill
      // price; the screen offers the cost price it can see, else leaves the
      // box for the server to fill (a reader without see_item_cost sees none).
      unitCost: item?.costPrice ?? '',
    });
  };

  const requestLines = (): GrnLineInput[] =>
    lines.map((line) => ({
      ...(line.poLineNo === null ? { itemId: line.itemId } : { poLineNo: line.poLineNo }),
      // Wave 5C — the row's own warehouse when it differs from the header's.
      ...(line.warehouseId !== '' ? { warehouseId: line.warehouseId } : {}),
      ...(line.poLineNo === null && line.unitCost.trim() !== ''
        ? { unitCost: line.unitCost.trim() }
        : {}),
      qtyReceived: line.received.trim() === '' ? '0' : line.received.trim(),
      quality: line.quality,
      ...(line.quality === 'partially_accepted' && line.acceptedTyped.trim() !== ''
        ? { acceptedQty: line.acceptedTyped.trim() }
        : {}),
      ...(parseSerials(line.serialsText).length > 0
        ? { serialNos: parseSerials(line.serialsText) }
        : {}),
      ...(line.batchNo.trim() !== '' ? { batchNo: line.batchNo.trim() } : {}),
      ...(line.note.trim() !== '' ? { note: line.note.trim() } : {}),
    }));

  const onCreate = async () => {
    setError(null);
    if (isDirect) {
      // Wave 5C: no order — the supplier is the receipt's own fact.
      if (!partnerId) {
        setError(t('grn.errNoSupplier'));
        return;
      }
    } else if (!purchaseOrderId) {
      // ERR-PUR-02-01's shape: type an order first.
      setError(t('grn.errNoOrder'));
      return;
    }
    if (!warehouseId) {
      setError(t('grn.errNoWarehouse'));
      return;
    }
    setBusy(true);
    try {
      const created = await createReceipt.mutateAsync({
        ...(isDirect ? { partnerId } : { purchaseOrderId }),
        receiptDate,
        warehouseId,
        ...(supplierRef.trim() ? { supplierRef: supplierRef.trim() } : {}),
        ...headerForCreate(header),
        ...(notes.trim() ? { notes } : {}),
      });
      void navigate(`/purchases/receipts/${created.id}`, { replace: true });
    } catch (thrown) {
      // A 403 on the direct shape is the direct SWITCH being refused, not
      // purchasing.create — the screen opened; the shape did not.
      if (thrown instanceof ApiError && thrown.status === 403)
        denyPermission(isDirect ? RECEIVE_WITHOUT_ORDER : 'purchasing.create');
      // The line above already reads the cause to spot a 403; every strip on
      // this screen used to throw that same cause away one expression later.
      // `localizeServerCause` keeps it: a refusal that ARRIVED keeps the
      // server's sentence, localized, and a request that never came back gets
      // this screen's own sentence rather than the browser's `Failed to fetch`.
      // All five paths name `grn.errSaveFailed` — one screen, one sentence.
      setError(localizeServerCause(t, thrown, 'grn.errSaveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const onSave = async (): Promise<boolean> => {
    setError(null);
    setBusy(true);
    try {
      await updateReceipt.mutateAsync({
        receiptDate,
        ...(warehouseId ? { warehouseId } : {}),
        supplierRef: supplierRef.trim() === '' ? null : supplierRef.trim(),
        ...headerForUpdate(header),
        lines: requestLines(),
        notes: notes.trim() === '' ? null : notes,
      });
      loadedId.current = null;
      return true;
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('purchasing.edit');
      setError(localizeServerCause(t, thrown, 'grn.errSaveFailed'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const onPost = async () => {
    if (!(await onSave())) return;
    setBusy(true);
    try {
      await postReceipt.mutateAsync();
      loadedId.current = null;
      setPosted(true);
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403)
        denyPermission('purchasing.approve');
      setError(localizeServerCause(t, thrown, 'grn.errSaveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setDeleting(false);
    if (!receiptId) return;
    setBusy(true);
    setError(null);
    try {
      await deleteReceipt.mutateAsync();
      void navigate('/purchases/receipts');
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('purchasing.delete');
      setError(localizeServerCause(t, thrown, 'grn.errSaveFailed'));
    } finally {
      setBusy(false);
    }
  };

  /**
   * BR-PUR-02-08's tail / GEN-72: cancelling a posted receipt asks WHY —
   * the ReasonBox IS the confirm (ADM-02 §8: no browser chrome; the typed
   * reason makes it §8's own-dialog pattern). Refused server-side once a
   * posted bill is matched (BR-PUR-02-09, the bill named in the sentence).
   */
  const onCancelPosted = async (reason: string) => {
    if (!receiptId) return;
    setCancelAsk(false);
    setBusy(true);
    setError(null);
    try {
      await cancelReceipt.mutateAsync(reason);
      loadedId.current = null;
      setPosted(false);
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403)
        denyPermission('purchasing.approve');
      setError(localizeServerCause(t, thrown, 'grn.errSaveFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (!isNew && loading) {
    return <div className="h-64 animate-pulse rounded-xl bg-muted" aria-busy />;
  }

  /**
   * PUR-02's two footers, and NEITHER carries a unit — `N-14`: these are Σ
   * over LINES, and one GRN can receive 200.000 kg of sheet and 4 boxes of
   * screws. "Adding 3 kg to 4 boxes is meaningless", so there is no unit that
   * would be true of the figure and no unit's decimals to round it to. Every
   * ROW prints its unit, which is where `N-4` puts it.
   *
   * The received total is exact BigInt on the 3-decimal strings. It was
   * `Number`, and a float sum in a quantity footer can print 0.30000000000004
   * — a figure no line on the note contains (DECISION.md §2.1: quantities are
   * strings end to end).
   */
  const totalOrdered = receipt?.totalOrdered ?? '0';
  const totalReceived = (() => {
    let total = 0n;
    for (const line of lines) total += toMilliQty(line.received.trim()) ?? 0n;
    return trimZeros(`${total / 1000n}.${(total % 1000n).toString().padStart(3, '0')}`);
  })();
  const rejectedLines = rejectedLineCount(lines);
  /**
   * Wave 5C — the "Accepted value" footer is Σ over the LINES as typed
   * (accepted × unit cost, each to the cent), so it moves with the grid;
   * once posted it is the server's snapped figure, which is what the ledger
   * carries.
   */
  const acceptedValue = editable
    ? acceptedValueOf(
        lines.map((line) => ({
          acceptedQty: acceptedQtyFor(line.quality, line.received.trim() || '0', line.acceptedTyped.trim() || '0'),
          unitCost: line.unitCost,
        })),
      )
    : (receipt?.acceptedValue ?? '0.00');
  const headerWarehouseName =
    activeWarehouses.find((warehouse) => warehouse.id === warehouseId)?.name ??
    receipt?.warehouseName ??
    '';

  return (
    <div className="grid gap-4">
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 bg-background">
        <h2 className="text-lg font-bold">{t('grn.editorTitle')}</h2>
        <span className="rounded-md bg-muted px-2 py-1 font-mono text-sm text-muted-foreground">
          {receipt ? <bdi dir="ltr">{receipt.code}</bdi> : t('grn.codeNew')}
        </span>
        <DocStatusChip status={status} />
        {/* BR-PUR-02-11: the same predicate the list draws, live against the
            date box — a draft whose date is corrected to the promised day
            drops the chip before saving. The title carries both dates. */}
        {receipt && receiptIsLate({ expectedDate: receipt.expectedDate, receiptDate }) ? (
          <span
            className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive"
            title={t('grn.lateTitle', { expected: receipt.expectedDate, received: receiptDate })}
          >
            {t('grn.lateChip')}
          </span>
        ) : null}
        {/* The toolbar's acts, DECLARED — see `document-toolbar.tsx`. */}
        <DocumentToolbar
          header={{ kind: 'goodsReceipt', id: receiptId, dirty }}
          actions={[
            {
              // Not PART 4.6's Cancel row: a link back to the list wearing the
              // same word. The primitive's header records the collision.
              id: 'back',
              row: null,
              label: t('common.cancel'),
              variant: 'outline',
              to: '/purchases/receipts',
            },
            {
              // Wave 2 — the invoice's Print/PDF pair on the goods receipt
              // (counts and the quality gate, no money). Saved receipts only.
              id: 'printPdf',
              row: 'print',
              node:
                !isNew && receipt ? (
                  <DocumentActions
                    request={{ kind: 'goods_receipt', locale: printLocale, subjectId: receipt.id }}
                    payload={{ kind: 'goods_receipt', data: goodsReceiptDocument(receipt) }}
                  />
                ) : null,
            },
            {
              // The document's question and the role's, kept apart (16.7).
              id: 'delete',
              row: 'delete',
              label: t('common.delete'),
              icon: <Trash2 aria-hidden />,
              variant: 'destructive',
              visible: !isNew && isDraft,
              gate: { permission: 'purchasing.delete', reasonKey: 'perm.cannot.deleteReceipts' },
              disabled: busy,
              onAct: () => setDeleting(true),
            },
            {
              id: 'create',
              row: null,
              // Wave 5C: a direct receipt has no order to load from.
              label: isDirect ? t('grn.createDirect') : t('grn.loadFromPo'),
              visible: isNew,
              gate: { permission: 'purchasing.create', reasonKey: 'perm.cannot.createReceipts' },
              busy,
              onAct: () => void onCreate(),
            },
            {
              // `disabled`, not `busy`: this Save never drew a spinner, and
              // GEN-60's pair travels together or not at all — declaring
              // `busy` here would ADD a glyph the screen has never shown.
              id: 'save',
              row: 'save',
              label: t('common.save'),
              variant: 'outline',
              visible: !isNew && isDraft,
              gate: { permission: 'purchasing.edit', reasonKey: 'perm.cannot.editReceipts' },
              disabled: busy,
              onAct: () => void onSave(),
            },
            {
              // Posting is `purchasing.approve` and nothing else. It used to
              // hang off `editable` too, which silently required
              // `purchasing.edit` as well — the receiving clerk who may
              // approve but not amend saw no button and no reason. The
              // declaration keeps this gate separate from Save's.
              id: 'post',
              row: 'confirm',
              label: t('grn.post'),
              icon: <PackageCheck aria-hidden />,
              visible: !isNew && isDraft,
              gate: { permission: 'purchasing.approve', reasonKey: 'perm.cannot.postReceipts' },
              busy,
              onAct: () => void onPost(),
            },
            {
              // Wave 1C — the next paper after the lorry: the supplier's bill,
              // carried from the same order (the bill editor seeds `?poId=`).
              id: 'createBill',
              row: null,
              label: t('grn.createBill'),
              icon: <ReceiptText aria-hidden />,
              variant: 'outline',
              visible: !isNew && status === 'posted',
              to: `/purchases/bills/new?poId=${receipt?.purchaseOrderId ?? ''}`,
            },
            {
              // BR-PUR-02-08's tail — the posting act a posted receipt
              // offers. Gated exactly like Post. Gone once cancelled.
              id: 'cancel-posted',
              row: 'cancel',
              label: t('grn.cancelPosted'),
              icon: <Trash2 aria-hidden />,
              variant: 'destructive',
              visible: !isNew && status === 'posted',
              gate: { permission: 'purchasing.approve', reasonKey: 'perm.cannot.cancelReceipts' },
              disabled: busy,
              onAct: () => setCancelAsk(true),
            },
          ]}
        >
          {/* GEN-58 asks its sentence in the product’s ONE shared box
              (GEN-204, GEN-209 — "deleting a draft" is first on its list).
              `window.confirm` was neither of BR-PERM-15’s two patterns.
              Inside the same guard as the button, so the dialog cannot
              outlive the condition that allows the delete. */}
          {!isNew && isDraft ? (
            <DeleteConfirmBox
              open={deleting}
              detail={t('grn.confirmDelete')}
              onCancel={() => setDeleting(false)}
              onConfirm={() => void onDelete()}
              busy={busy}
            />
          ) : null}
          {/* GEN-72's typed reason. Unguarded exactly as it was — its own
              `open` is the only thing that draws it, and moving it under the
              posted ternary would be a behaviour change, not a tidy-up. */}
          <ReasonBox
            open={cancelAsk}
            title={t('grn.cancelPosted')}
            prompt={t('grn.cancelPrompt')}
            reasonLabel={t('gen72.cancelReasonLabel')}
            confirmLabel={t('gen72.confirmCancel')}
            busy={busy}
            onConfirm={(reason) => void onCancelPosted(reason)}
            onCancel={() => setCancelAsk(false)}
          />
        </DocumentToolbar>
      </div>

      {posted || status === 'posted' ? (
        <p className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
          {t('grn.postedNotice')}
        </p>
      ) : null}
      {/* Wave 5C — an import order's landed cost sits on this receipt's stock. */}
      {receipt?.importOrderId ? (
        <p className="rounded-md border border-dashed px-4 py-2 text-sm text-muted-foreground">
          {t('grn.landedFromImport')}
        </p>
      ) : null}
      {status === 'cancelled' ? (
        <p className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          {t('grn.cancelledNotice', { reason: receipt?.cancelReason ?? '' })}
        </p>
      ) : null}
      {error ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      ) : null}

      {/* Wave 5C — the receipt's shape, chosen before anything else on a new
          one: against an order (PUR-02's own), or direct from a supplier for
          goods nobody ordered. Offered only to the role that holds the
          switch; the server refuses the shape by name regardless. */}
      {isNew ? (
        <div
          role="radiogroup"
          aria-label={t('grn.modeLabel')}
          className="flex flex-wrap gap-2"
        >
          <Button
            type="button"
            role="radio"
            aria-checked={!isDirect}
            variant={!isDirect ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMode('order')}
          >
            {t('grn.modeFromOrder')}
          </Button>
          {/* 16.7 row 2: greyed with its reason, never hidden — a clerk who
              cannot see the choice cannot tell whether it exists. */}
          <Gated permission={RECEIVE_WITHOUT_ORDER} reasonKey="perm.cannot.receiveWithoutOrder">
            <Button
              type="button"
              role="radio"
              aria-checked={isDirect}
              variant={isDirect ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMode('direct')}
            >
              {t('grn.modeDirect')}
            </Button>
          </Gated>
        </div>
      ) : null}
      {isDirect ? (
        <p className="rounded-md border border-dashed px-4 py-2 text-sm text-muted-foreground">
          {t('grn.directNotice')}
        </p>
      ) : null}

      <Card>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="grid gap-1.5">
            <Label htmlFor="grn-order">{isDirect ? t('grn.fSupplier') : t('grn.fOrder')}</Label>
            {isNew && isDirect ? (
              <PartnerTypeahead
                entity="supplier"
                id="grn-order"
                label={t('grn.fSupplier')}
                value={partnerId || null}
                onChange={(id) => setPartnerId(id ?? '')}
                options={supplierPicker.options}
                selected={supplierPicker.optionFor(partnerId)}
                query={supplierPicker.query}
                onQueryChange={supplierPicker.setQuery}
                placeholder={t('grn.pickSupplier')}
                emptyLabel={t('picker.noMatchingSupplier')}
                loading={supplierPicker.loading}
              />
            ) : isNew ? (
              <Typeahead
                id="grn-order"
                label={t('grn.fOrder')}
                value={purchaseOrderId || null}
                onChange={(id) => setPurchaseOrderId(id ?? '')}
                options={orderPicker.options}
                selected={orderPicker.optionFor(purchaseOrderId)}
                query={orderPicker.query}
                onQueryChange={orderPicker.setQuery}
                placeholder={t('grn.pickOrder')}
                emptyLabel={t('picker.noMatchingOrder')}
                loading={orderPicker.loading}
              />
            ) : (
              <div className="flex h-12 items-center rounded-[11px] border border-input bg-muted/40 px-3 font-mono text-sm">
                {isDirect ? (
                  <span className="truncate font-sans">{receipt?.partnerName}</span>
                ) : (
                  <>
                    <Ltr value={receipt?.purchaseOrderCode ?? ''} />
                    <span className="ms-auto truncate ps-2 font-sans text-muted-foreground">
                      {receipt?.partnerName}
                    </span>
                  </>
                )}
              </div>
            )}
            {/* Wave 5C — what we owe this supplier, beside their name. */}
            <SupplierBalanceChip
              partnerId={isNew ? partnerId : receipt?.partnerId}
              side="supplier"
              className="justify-self-start"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="grn-date">{t('grn.fDate')}</Label>
            <Input
              id="grn-date"
              type="date"
              dir="ltr"
              value={receiptDate}
              onChange={(event) => setReceiptDate(event.target.value)}
              disabled={!isNew && !editable}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="grn-warehouse">{t('grn.fWarehouse')}</Label>
            <NativeSelect
              id="grn-warehouse"
              value={warehouseId}
              onChange={(event) => setWarehouseId(event.target.value)}
              disabled={!isNew && !editable}
            >
              <option value="">{t('grn.pickWarehouse')}</option>
              {activeWarehouses.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="grn-ref">{t('grn.fSupplierRef')}</Label>
            <Input
              id="grn-ref"
              dir="ltr"
              className="font-mono"
              value={supplierRef}
              onChange={(event) => setSupplierRef(event.target.value)}
              disabled={!isNew && !editable}
              maxLength={60}
            />
          </div>
          {/* Wave 5C — the lorry's paper: the supplier's DELIVERY NOTE (a
              different paper from their invoice above), who received and who
              checked, the driver and vehicle at the door, the paper book. */}
          <div className="grid gap-1.5">
            <Label htmlFor="grn-dn-no">{t('grn.fDeliveryNoteNo')}</Label>
            <Input
              id="grn-dn-no"
              dir="ltr"
              className="font-mono"
              value={header.supplierDeliveryNoteNo}
              onChange={(event) => setHeader({ ...header, supplierDeliveryNoteNo: event.target.value })}
              disabled={headerLocked}
              maxLength={60}
            />
          </div>
          <PersonSelect
            id="grn-received-by"
            label={t('grn.fReceivedBy')}
            value={header.receivedByUserId}
            heldName={receipt?.receivedByName ?? null}
            onChange={(userId) => setHeader({ ...header, receivedByUserId: userId })}
            disabled={headerLocked}
            emptyLabel={t('grn.nobody')}
          />
          <PersonSelect
            id="grn-checked-by"
            label={t('grn.fCheckedBy')}
            value={header.checkedByUserId}
            heldName={receipt?.checkedByName ?? null}
            onChange={(userId) => setHeader({ ...header, checkedByUserId: userId })}
            disabled={headerLocked}
            emptyLabel={t('grn.nobody')}
          />
          <div className="grid gap-1.5">
            <Label htmlFor="grn-driver">{t('grn.fDriver')}</Label>
            <Input
              id="grn-driver"
              value={header.driver}
              onChange={(event) => setHeader({ ...header, driver: event.target.value })}
              disabled={headerLocked}
              maxLength={120}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="grn-vehicle">{t('grn.fVehicle')}</Label>
            <Input
              id="grn-vehicle"
              dir="ltr"
              className="font-mono"
              value={header.vehicleNo}
              onChange={(event) => setHeader({ ...header, vehicleNo: event.target.value })}
              disabled={headerLocked}
              maxLength={60}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="grn-manual-ref">{t('grn.fManualRef')}</Label>
            <Input
              id="grn-manual-ref"
              dir="ltr"
              className="font-mono"
              value={header.manualRef}
              onChange={(event) => setHeader({ ...header, manualRef: event.target.value })}
              disabled={headerLocked}
              maxLength={60}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="grn-book-no">{t('grn.fBookNo')}</Label>
            <Input
              id="grn-book-no"
              dir="ltr"
              className="font-mono"
              value={header.bookNo}
              onChange={(event) => setHeader({ ...header, bookNo: event.target.value })}
              disabled={headerLocked}
              maxLength={40}
            />
          </div>
        </CardContent>
      </Card>

      {!isNew ? (
        <Card>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-11 text-center">#</TableHead>
                  <TableHead>{t('grn.colItem')}</TableHead>
                  {/* Wave 1C — Ordered / Received before / Remaining: the
                      second container reads what the first already took.
                      Wave 5C: a direct receipt ordered nothing — no columns
                      about an order it does not have. */}
                  {isDirect ? null : (
                    <>
                      <TableHead className="w-24 text-end">{t('grn.colOrdered')}</TableHead>
                      <TableHead className="w-24 text-end">{t('grn.colReceivedBefore')}</TableHead>
                      <TableHead className="w-24 text-end">{t('grn.colRemaining')}</TableHead>
                    </>
                  )}
                  <TableHead className="w-28 text-end">{t('grn.colReceivedQty')}</TableHead>
                  <TableHead className="w-40">{t('grn.colQuality')}</TableHead>
                  <TableHead className="w-28 text-end">{t('grn.colAccepted')}</TableHead>
                  {/* Wave 5C — where the row lands, what a unit entered at,
                      and the row's value: the three the review found missing. */}
                  <TableHead className="min-w-[140px]">{t('grn.colWarehouse')}</TableHead>
                  <TableHead className="w-28 text-end">{t('grn.colUnitCost')}</TableHead>
                  <TableHead className="w-28 text-end">{t('grn.colLineValue')}</TableHead>
                  <TableHead className="min-w-[160px]">{t('grn.colTracking')}</TableHead>
                  <TableHead className="min-w-[140px]">{t('grn.colNote')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((line, index) => {
                  // Wave 1C: short of what was still OUTSTANDING, not of the
                  // whole order (grn-rules.ts).
                  const backorder = isBackorder(line.received, line.remaining);
                  const acceptedNow = acceptedQtyFor(
                    line.quality,
                    line.received.trim() || '0',
                    line.acceptedTyped.trim() || '0',
                  );
                  return (
                    <TableRow
                      key={line.key}
                      className={cn(
                        'odd:bg-muted/30',
                        backorder && 'bg-amber-50 odd:bg-amber-50 dark:bg-amber-950/40 dark:odd:bg-amber-950/40',
                      )}
                    >
                      <TableCell className="text-center font-mono text-muted-foreground">
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        {editable && line.poLineNo === null ? (
                          /* Wave 5C — a free line names its own item. */
                          <div className="flex items-center gap-1">
                            <ItemCell
                              label={t('grn.cellItem', { line: index + 1 })}
                              picker={itemPicker}
                              value={line.itemId}
                              onPick={(itemId) => onPickItem(line.key, itemId)}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={t('grn.removeLine', { line: index + 1 })}
                              onClick={() => removeLine(line.key)}
                            >
                              <X aria-hidden />
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div className="font-medium">{line.description}</div>
                            <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
                              <Ltr value={line.itemCode} />
                              {backorder ? (
                                <span className="rounded bg-amber-200 px-1.5 py-0.5 font-sans text-amber-900 dark:bg-amber-800 dark:text-amber-100">
                                  {t('grn.backorder')}
                                </span>
                              ) : null}
                            </div>
                          </>
                        )}
                      </TableCell>
                      {/* `N-4` — what the ORDER asked for, in its own unit.
                          It was a bare trimmed string: "200" for a line that
                          means 200.000 kg, in a column whose rows can be
                          kilograms and boxes at once (`N-14`). */}
                      {isDirect ? null : (
                        <>
                          <TableCell className="text-end">
                            <Qty
                              value={line.ordered}
                              precision={line.unitPrecision}
                              unit={line.unitSymbol}
                            />
                          </TableCell>
                          <TableCell className="text-end text-muted-foreground">
                            <Qty
                              value={line.alreadyReceived}
                              precision={line.unitPrecision}
                              unit={line.unitSymbol}
                            />
                          </TableCell>
                          <TableCell className="text-end">
                            <Qty
                              value={line.remaining}
                              precision={line.unitPrecision}
                              unit={line.unitSymbol}
                              className="font-semibold"
                            />
                          </TableCell>
                        </>
                      )}
                      {/*
                        * What ARRIVED. A confirmed GRN is locked — the goods
                        * are on the shelf and the correction is another
                        * document — so past draft this box can never be typed
                        * into again and reads as the record it is.
                        */}
                      <TableCell className={editable ? undefined : 'text-end'}>
                        {editable ? (
                          <CellInput
                            mono
                            dir="ltr"
                            inputMode="decimal"
                            aria-label={t('grn.cellReceived', { line: index + 1 })}
                            value={line.received}
                            onChange={(event) =>
                              setLine(line.key,{ received: event.target.value })
                            }
                          />
                        ) : (
                          <Qty
                            value={line.received}
                            precision={line.unitPrecision}
                            unit={line.unitSymbol}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <NativeSelect
                          aria-label={t('grn.cellQuality', { line: index + 1 })}
                          className={cn(
                            'h-9 text-sm',
                            line.quality === 'accepted' && 'text-emerald-700 dark:text-emerald-400',
                            line.quality === 'rejected' && 'text-red-700 dark:text-red-400',
                            line.quality === 'partially_accepted' && 'text-amber-700 dark:text-amber-400',
                          )}
                          value={line.quality}
                          onChange={(event) =>
                            setLine(line.key,{ quality: event.target.value as GrnQuality })
                          }
                          disabled={!editable}
                        >
                          <option value="accepted">{t('grn.quality.accepted')}</option>
                          <option value="rejected">{t('grn.quality.rejected')}</option>
                          <option value="partially_accepted">
                            {t('grn.quality.partially_accepted')}
                          </option>
                        </NativeSelect>
                      </TableCell>
                      <TableCell>
                        {line.quality === 'partially_accepted' ? (
                          <CellInput
                            mono
                            dir="ltr"
                            inputMode="decimal"
                            aria-label={t('grn.cellAccepted', { line: index + 1 })}
                            value={line.acceptedTyped}
                            onChange={(event) =>
                              setLine(line.key,{ acceptedTyped: event.target.value })
                            }
                            disabled={!editable}
                          />
                        ) : (
                          <div className="pe-2 text-end text-muted-foreground">
                            {/* `N-4` — BR-PUR-02-04's derived accepted
                                quantity. It is two cells from the received
                                figure with the Quality picker between them,
                                so nothing beside it names the unit and it
                                carries its own. */}
                            <Qty
                              value={acceptedQtyFor(line.quality, line.received || '0', '')}
                              precision={line.unitPrecision}
                              unit={line.unitSymbol}
                            />
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {/* Wave 5C — the row's own warehouse; the blank
                            choice is the header's, named so the clerk can
                            see where "default" is. */}
                        <NativeSelect
                          aria-label={t('grn.cellWarehouse', { line: index + 1 })}
                          className="h-9 text-sm"
                          value={line.warehouseId}
                          onChange={(event) => setLine(line.key, { warehouseId: event.target.value })}
                          disabled={!editable}
                        >
                          <option value="">{headerWarehouseName}</option>
                          {activeWarehouses
                            .filter((warehouse) => warehouse.id !== warehouseId)
                            .map((warehouse) => (
                              <option key={warehouse.id} value={warehouse.id}>
                                {warehouse.name}
                              </option>
                            ))}
                        </NativeSelect>
                      </TableCell>
                      <TableCell className="text-end">
                        {editable && line.poLineNo === null ? (
                          <CellInput
                            mono
                            dir="ltr"
                            inputMode="decimal"
                            aria-label={t('grn.cellUnitCost', { line: index + 1 })}
                            value={line.unitCost}
                            onChange={(event) => setLine(line.key, { unitCost: event.target.value })}
                          />
                        ) : (
                          <Amount value={line.unitCost} currency={baseCurrency} decimals={2} />
                        )}
                      </TableCell>
                      <TableCell className="text-end">
                        <Amount
                          value={lineValueOf(acceptedNow, line.unitCost)}
                          currency={baseCurrency}
                          className="font-medium"
                        />
                      </TableCell>
                      <TableCell>
                        <CellInput
                          dir="ltr"
                          className="font-mono"
                          aria-label={t('grn.cellTracking', { line: index + 1 })}
                          placeholder={t('grn.trackingPh')}
                          value={line.serialsText !== '' ? line.serialsText : line.batchNo}
                          onChange={(event) => {
                            const value = event.target.value;
                            // One box, two meanings: a comma splits serials,
                            // a single token is a lot number. The server
                            // enforces which one the item's tracking wants.
                            if (value.includes(',')) {
                              setLine(line.key,{ serialsText: value, batchNo: '' });
                            } else {
                              setLine(line.key,{ serialsText: value, batchNo: value });
                            }
                          }}
                          disabled={!editable}
                        />
                      </TableCell>
                      <TableCell>
                        <CellInput
                          aria-label={t('grn.cellNote', { line: index + 1 })}
                          placeholder={t('grn.notePh')}
                          value={line.note}
                          onChange={(event) => setLine(line.key,{ note: event.target.value })}
                          disabled={!editable}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={2}>
                    {rejectedLines > 0 ? (
                      <span className="text-sm font-medium text-red-700 dark:text-red-400">
                        {t('grn.rejectedNote', { count: rejectedLines })}
                      </span>
                    ) : null}
                  </TableCell>
                  {isDirect ? null : (
                    <TableCell className="text-end">
                      <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                        {t('grn.qtyOrdered')}
                      </span>{' '}
                      <bdi dir="ltr" className="font-mono font-semibold">
                        {totalOrdered}
                      </bdi>
                    </TableCell>
                  )}
                  <TableCell colSpan={isDirect ? 8 : 10} className="text-start">
                    <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                      {t('grn.qtyReceived')}
                    </span>{' '}
                    <bdi dir="ltr" className="font-mono font-semibold">
                      {totalReceived}
                    </bdi>
                    <span className="ms-4 text-sm text-muted-foreground" data-testid="grn-accepted-value">
                      {t('grn.acceptedValue')}{' '}
                      <Amount value={acceptedValue} currency={baseCurrency} className="font-semibold" />
                    </span>
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
            {/* Wave 5C — a direct receipt's grid is typed, not loaded. */}
            {editable && isDirect ? (
              <Button type="button" variant="outline" size="sm" className="mt-3" onClick={addFreeLine}>
                <Plus aria-hidden /> {t('grn.addLine')}
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {/* Wave 5C — the receipt's history card (the bill card's shape). */}
      {!isNew ? (
        <DocumentHistoryCard
          entries={history.data?.entries ?? []}
          loading={history.isPending}
          emptyText={t('grn.historyEmpty')}
        />
      ) : null}

      <Card>
        <CardContent className="grid gap-1.5">
          <Label htmlFor="grn-notes">{t('sales.notes')}</Label>
          <Textarea
            id="grn-notes"
            maxLength={2000}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            disabled={!isNew && !editable && status !== 'posted'}
          />
        </CardContent>
      </Card>
      {/* Wave 2 — INV-H5's files panel on this document. Hidden
          behind "save first" on a new draft; permission follows the record. */}
      <AttachmentsPanel kind="goods_receipt" entityId={receiptId} />
    </div>
  );
}
